import React,{  useState } from 'react';
import * as yup from 'yup';
import  {useTranslation} from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { ApiProvider } from '../providers/apiProvider';
import AssignmentInfo from '../components/workerForm/assignmentInformation';
import {ClaycoForm, SpinnerButton } from 'clayco-controls'; 
import WorkHistory from '../components/workerForm/workHistory';
import WorkerInformation from '../components/workerForm/workerInformation';
import VoluntaryInfo from '../components/workerForm/voluntaryInformation';
import Certificates from '../components/workerForm/certificates';
import EmergencyContact from '../components/workerForm/emergencyContact';
import LegalText from '../components/legalText';
import FormAlert from '../components/formElements/formAlert';
import PhoneNumberValidation from '../components/phoneNumberValidation';
import WorkerMatchVerification from '../components/workerMatchVerification';
import { useNavigate } from 'react-router-dom';
import moment from 'moment/moment';
import { RAW_PHONE } from "constants/values";
import LoadingIndicator from "../components/loadingIndicator";
import FormValidationMessage from '../components/formValidationMessage';
import { log } from 'utils/logging';


const Acknowledgement = ({ loadingChange }) => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [sendingSMS, setSendingSMS] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [alertText] = useState("");
    const {instance, accounts} = useMsal();
    const _api = new ApiProvider(instance, accounts[0]);
    
    const [savedAssociateIds, setSavedAssociateIds] = useState();
    const [submittedPhoneResult, setSubmittedPhoneResult] = useState();
    
    // display modal flags
    const [showWorkerVerificationModal, setShowWorkerVerificationModal] = useState(false);
    const [showValidateModal, setShowValidationModal] = useState(false);
    const [submitClicked, setSubmitClicked] = useState(false);

    // match conditions
    const [disableMatchFields, setDisableMatchFields] = useState(false);
    const [matchAttemptComplete, setMatchAttemptComplete] = useState(false);
    const [matchesFound, setMatchesFound] = useState(false);
    const [onlyPreloadedMatchFound, setOnlyPreloadedMatchFound] = useState(false);
    const [workerVerified, setWorkerVerified] = useState(false);
    const [allMatchesConfirmed, setAllMatchesConfirmed] = useState(false);
    const [noMatchFoundOnCurrentLastAndPhone, setNoMatchFoundOnCurrentLastAndPhone] = useState(false);

    // match data
    const [matchedWorkers, setMatchedWorkers] = useState([]);
    const [yesMatchedAssociates, setYesMatchedAssociates] = useState([]);
    const [mostRecentYesMatchedAssociate, setMostRecentYesMatchedAssociate] = useState(null);
    const [verifyLastName, setVerifyLastName] = useState(null);
    const [verifyPhoneNumber, setVerifyPhoneNumber] = useState(null);
    const [workerAuthorizationCode, setWorkerAuthorizationCode] = useState(null);
    const [preloadedMatch, setPreloadedMatch] = useState(null);
    const [preloadedAssignment, setPreloadedAssignment] = useState(null);
    const [preloadedAssociate, setPreloadedAssociate] = useState(null);

    const formSchema = yup.object().shape({
        workerInfo: yup.object({
            firstName: yup.string().nullable(false).required(t('Required')),
            lastName: yup.string().nullable(false).required(t('Required')),
            phoneNumber: yup.string()
                .when("noPhone", {is: !true, then: 
                    yup.string().nullable(false)
                        .required(t('Required'))
                        .test("", t("Invalid phone number"), (item) => item ? item.length === 12 : true)
                        .test("", t("PhoneStartingCharacter"), (item) => /^(?![01])\d+$/.test(item?.charAt(0)))}),
            smsConsent: yup.boolean().nullable(false).default(true), //This defaults to true since WorkerInformation component starts as true and we don't want to trigger a validation error on load. The user can uncheck if they do not consent.
            emergencyContact: yup.object().shape({
                name: yup.string().nullable(false).required(t('Required')),
                phone: yup.string().required(t('Required'))
                    .test("", t("Invalid phone number"), (item) => item ? item.length === 12 : true)
                    .test("", t("PhoneStartingCharacter"), (item) => /^(?![01])\d+$/.test(item?.charAt(0))),
            }),
            noPhone: yup.bool().default(false).transform(( function(value, originalValue) { if (!value) { return false; } return value; })),
        }),
        assignmentInfo: yup.object({
            project: yup.mixed().nullable(false).required(t('Required')),
            projectNumber: yup.string().nullable(false).required(t('Required')),
            organization: yup.number().nullable(false).typeError('Required').required('Required'),
            employerId: yup.string().nullable(false).required(t('Required')),
            employer: yup.mixed(),
            role: yup.string().nullable(false).required(t('Required')),
            craft: yup.string().nullable(false).required(t('Required'))
        }),
        // jobsiteId: yup.array().of(yup.object({
        //     value: yup.string().nullable(false).test('required', t('Required'), (value, ctx) => { 
        //         const typeValue = ctx.parent.jobsiteIdType;
        //         const hasType =  typeValue != null && typeValue !== '';
        //         const hasValue = (value != null && value.trim() !== '');
        //         return (!hasType || hasValue);
        //     })
        // })).default([]),
        voluntaryInfo: yup.object({
            ethnicity: yup.string().nullable(),
            gender: yup.string().nullable(),
            veteran: yup.string().default("No"),
            zipCode: yup.string().optional(true).test("", t("Invalid zipcode"), (item) => item ? item.length === 5 : true),
            profilePhoto: yup.mixed().nullable(true)
        }),
        certification: yup.array().of(yup.object({
            id: yup.number().nullable(true),
            name: yup.string().nullable(true),
            label: yup.string().nullable(true),
            fileList: yup.mixed().test('required', t('Required'), (fileData, ctx) => { 
                const checked = ctx.parent.checked;
                const hasFileData = fileData != null && fileData.length > 0;
                const isOkay = !checked || hasFileData;
                return isOkay;
            }),
            expiration: yup.mixed().test('required', 'Required', (value, ctx) => { 
                const hasValue = (value != null && value !== '');
                const checkedAndExpires = ctx.parent.checkedAndExpires;
                return (!checkedAndExpires || hasValue);
            })
        })).default([]),
        signature: yup.string().nullable(true).required(t('Required'))
        
    });

    const resetMatchVerification = () => {
        setMatchedWorkers([]);
        setMatchesFound(false);
        setOnlyPreloadedMatchFound(false);
        setYesMatchedAssociates([]);
        setAllMatchesConfirmed(false);
        setMostRecentYesMatchedAssociate(null);
        setShowWorkerVerificationModal(false);
        setMatchAttemptComplete(false);
        setNoMatchFoundOnCurrentLastAndPhone(false);
        setPreloadedMatch(null);
        setPreloadedAssignment(null);
        setPreloadedAssociate(null);
    }

    const testMatch = async (lastName, phoneNumber) => { 
        resetMatchVerification();
        setVerifyLastName(lastName);
        setVerifyPhoneNumber(phoneNumber);

        if (lastName === '' || phoneNumber === '')
            return; // nothing to do

        loadingChange(true);
        const associateMatches = await _api.Get(`associate/checkformatches/${lastName}/${phoneNumber}`);
        loadingChange(false);
        if (associateMatches.error != null){
            console.error(associateMatches.error);
            return;
        }

        const matchCount = associateMatches.length;
        const hasMatches = matchCount > 0;
        const preloadedMatches = hasMatches ? associateMatches.filter(r => r.associate.isPreloaded) : [];
        const preloadedMatchCount = preloadedMatches.length;
        const hasPreloadedMatches = preloadedMatchCount > 0;
        const hasOnlyPreloadedMatches = hasMatches && preloadedMatchCount === matchCount;
        const preloadedMatch = hasPreloadedMatches ? preloadedMatches[preloadedMatchCount-1] : null;

        log.trace('preloadedMatch', preloadedMatch);

        setMatchedWorkers(associateMatches);
        setMatchesFound(hasMatches);
        setOnlyPreloadedMatchFound(hasOnlyPreloadedMatches);
        setNoMatchFoundOnCurrentLastAndPhone(!hasMatches);
        setPreloadedMatch(preloadedMatch);
        setPreloadedAssignment(preloadedMatch == null ? null : preloadedMatch.assignments[0]);
        setPreloadedAssociate(preloadedMatch == null ? null : preloadedMatch.associate);
        setShowWorkerVerificationModal(hasMatches && !workerVerified)
        setWorkerVerified(false);
        setMatchAttemptComplete(true);
    }
    
    const matchesConfirmed = (yesMatches) => {
        setYesMatchedAssociates(yesMatches);
        setAllMatchesConfirmed(true);
        if (yesMatches?.length > 0) {
            setMostRecentYesMatchedAssociate(yesMatches[yesMatches.length-1].associate)
        }
    }

    const resetMatchConfirmation = () => {
        setYesMatchedAssociates([]);
        setAllMatchesConfirmed(false);
        setMostRecentYesMatchedAssociate(null)
    }
        
    const showLoading = (isLoading) => {
        loadingChange(isLoading);
    };
    
    const addIfNull = (value) => {

        // Check for basic nulls
        if (value === null || value === undefined || value === 0 || value === "0" || value === "")
            return null;

        // Check for default values
        if (value === "Ethnicity" || value === "Gender" || value === "Veteran")
            return null;
        
        return value;
    }

    // const validJobsiteIds = (allJobsiteIds) => {
    //     const jobsiteIds = allJobsiteIds == null ? [] : allJobsiteIds.filter((r) => {
    //         return  r.jobsiteIdType != null && 
    //                 r.jobsiteIdType.trim() !== '' && 
    //                 r.value != null && 
    //                 r.value.trim() !== '';
    //     });

    //     return jobsiteIds;
    // }

    const createAssociate = async (form) => {
        
        // no matches presented = null
        // matches presented but no match selected = false
        // otherwise true
        const matchResponse = !matchesFound ? null : yesMatchedAssociates.length > 0 ? true : false;
        
        const params = {
            firstName: form.workerInfo.firstName,
            lastName: form.workerInfo.lastName,
            phoneNumber: form.workerInfo.phoneNumber.replace(/[^0-9\\.]+/g, ''),
            disableTextNotifications: !form.workerInfo.smsConsent,
            emergencyName: form.workerInfo.emergencyContact.name,
            emergencyPhone: form.workerInfo.emergencyContact.phone,    
            projectNumber: form.assignmentInfo.projectNumber,
            projectName: form.assignmentInfo.project.name,
            projectIdentifier: form.assignmentInfo.project.id,
            organization: form.assignmentInfo.employer.name,
            organizationId: form.assignmentInfo.employer.isIdSnowflake !== true ? form.assignmentInfo.employer.id : null,
            organizationSnowflakeId: form.assignmentInfo.employer.snowflakeId,
            roleId: form.assignmentInfo.role,
            ethnicityId: addIfNull(form.voluntaryInfo.ethnicity),
            genderId: addIfNull(form.voluntaryInfo.gender),
            zipCode: addIfNull(form.voluntaryInfo.zipCode),
            veteran: addIfNull(form.voluntaryInfo.veteran) === null ? null: (form.voluntaryInfo.veteran === "1"),
            signature: form.signature,
            craftId: form.assignmentInfo.craft,
            matchResponse: matchResponse,
        }

        const newAssociate = await _api.Post('associate/create', params);
        log.trace('createAssociate', 'newAssociate', newAssociate);

        if (newAssociate.error != null)
            return newAssociate;

        return {
            id: newAssociate.id,
            identifier: newAssociate.identifier,
            assignmentId: newAssociate.assignmentId 
        };
    }

    const updateAssociateProfile = async (form) => {
        
        const consolidationIds = yesMatchedAssociates?.reduce((results, matchChoice, i) => {
            if (matchChoice.associate.id !== mostRecentYesMatchedAssociate.id)
                results.push(matchChoice.associate.id);

            return results;
        }, []);

        const params = {
            id: mostRecentYesMatchedAssociate.id,
            firstName: form.workerInfo.firstName,
            lastName: form.workerInfo.lastName,
            phoneNumber: form.workerInfo.phoneNumber.replace(/[^0-9\\.]+/g, ''),
            emergencyId: mostRecentYesMatchedAssociate.emergencyContact.id,
            emergencyName: form.workerInfo.emergencyContact.name,
            emergencyPhone: form.workerInfo.emergencyContact.phone,
            ethnicityId: addIfNull(form.voluntaryInfo.ethnicity),
            genderId: addIfNull(form.voluntaryInfo.gender),
            zipCode: addIfNull(form.voluntaryInfo.zipCode),
            veteran: addIfNull(form.voluntaryInfo.veteran) === null ? null: (form.voluntaryInfo.veteran === "1"),
            authorizationCode: workerAuthorizationCode,
            signature: form.signature,
            consolidationIds: consolidationIds
        };

        return await _api.Post('associate/updatematchedprofile', params);

    }

    const createAssignmentForExistingAssociate = async (form, forAssociateId) => {

        //const jobsiteIds = validJobsiteIds(form.jobsiteIds);

        // note if we are creating the assignment here then match response must be true
        const params = {
            associateId: forAssociateId,
            projectNumber: form.assignmentInfo.projectNumber, 
            organizationName: form.assignmentInfo.employer.name,
            organizationId: form.assignmentInfo.employer.isIdSnowflake !== true ? form.assignmentInfo.employer.id : null,
            organizationSnowflakeId: form.assignmentInfo.employer.snowflakeId,
            roleId: form.assignmentInfo.role,
            craftId: form.assignmentInfo.craft,
            matchResponse: true,
            //jobsiteIds: jobsiteIds
        };

        log.trace('createAssignmentForExistingAssociate', 'params', params);

        const response = await _api.Post('assignment/create', params)
        return response
    }

    const updateAssociate = async (form) => {

        const updateProfileRequest = await updateAssociateProfile(form);
        if (updateProfileRequest.error != null)
            return updateProfileRequest;

        const newAssignmentId = await createAssignmentForExistingAssociate(form, mostRecentYesMatchedAssociate.id)
        if (newAssignmentId.error != null)
            return newAssignmentId;
        
        return {
            id: mostRecentYesMatchedAssociate.id,
            identifier: mostRecentYesMatchedAssociate.identifier,
            assignmentId: newAssignmentId
        };
    }

    const updatePhone = async (associateIdentifier, formattedPhone) => {
        
        const rawPhone = formattedPhone.replace(RAW_PHONE, '');

        const results = await _api.Post(`associate/updatephonenumber?associateId=${associateIdentifier}&phoneNumber=${rawPhone}`);
        if (results.error != null) {
            console.error(results.error);  
        }
    }

    const sendSMSMessage = async (associateIdentifier, formattedPhone) => {
        const rawPhone = formattedPhone.replace(RAW_PHONE, '');
        const results = await _api.Post(`phverify/sendnotification?phoneNumber=${rawPhone}&associateId=${associateIdentifier}`);
        
        if (results.error != null) {
            console.error(results.error);    
        }
        else {
            return results;
        }

    }

    const submitForm = async (formData) => {
        log.trace('submitForm', 'formData', formData);
        setSubmitting(true);

        try {
            const form = formSchema.cast(formData);

            const associateIds = mostRecentYesMatchedAssociate != null ?
                await updateAssociate(form) :
                await createAssociate(form);
            if (associateIds.error != null) {
                console.error(associateIds.error);
                return;
            }
            log.trace('submitForm', 'associateIds', associateIds);

            // const jobsiteIds = validJobsiteIds(form.jobsiteId);
            // for (let i = 0; i < jobsiteIds.length; i++){

            //     log.trace('submitForm', 'jobsiteIds', jobsiteIds);
            //     const jobsiteId = jobsiteIds[i];
            //     log.trace('submitForm', 'jobsiteId', jobsiteId);

            //     const jobsiteIdResults = await _api.Post(`jobsiteId/create/${associateIds.assignmentId}/${parseInt(jobsiteId.jobsiteIdType)}/${jobsiteId.value}`);
            //     if (jobsiteIdResults.error != null) {
            //         console.error(jobsiteIdResults.error);
            //         break;
            //     }
            // }

            if(form.voluntaryInfo.profilePhotoData !== null && form.voluntaryInfo.profilePhotoData !== undefined){
                var photoUpload = new FormData();
                photoUpload.append("profilePhoto", form.voluntaryInfo.profilePhotoData);
                const photoResults = await _api.Post(`associate/photo/upload/${associateIds.id}`, photoUpload, 'multipart/form-data')
                if (photoResults.error != null) {
                    console.error(photoResults.error);
                    return;
                }
            }

            let validCerts = form.certification.filter(x => x.checked === true);
            for (let i = 0; i < validCerts.length; i++){


                const certDate = validCerts[i].expiration == null || validCerts[i].expiration.trim() === ''
                    ? '' 
                    : moment(validCerts[i].expiration).format('YYYY-MM-DD');

                var uploadForm = new FormData();
                uploadForm.append("certName", validCerts[i].name);
                uploadForm.append("Certification", validCerts[i].fileData);
                uploadForm.append("fileName", validCerts[i].fileList[0].name);
                uploadForm.append("expirationDate", certDate);

                const certificationResults = await _api.Post(`certification/add/${validCerts[i].id}/${associateIds.id}`, uploadForm, 'multipart/form-data');
                if (certificationResults.error != null) {
                    console.error(certificationResults.error);
                    break;
                }
            }

            if(formData.workerInfo.smsConsent) {
                setSendingSMS(true);
                setSavedAssociateIds(associateIds);
                const smsResult = await sendSMSMessage(associateIds.identifier, form.workerInfo.phoneNumber);
                log.trace('smsResult', smsResult)
                setSubmittedPhoneResult(smsResult)
                setShowValidationModal(true);
            }
            else{
                navigate("/WorkerCreated")
            }
        }
        catch (error) {
            console.error('Something went wrong submitting the form: ');
            console.error(error);
        }
        finally {
            setSubmitting(false);
            setSendingSMS(false);
        }
    }

    const handleError = (error) => {
        console.error('Something went wrong submitting the form: ');
        console.error(error);
        setSubmitting(false);
    }

    const handleSuccessClose = () => {
        setShowAlert(false);
        
    };

    const handleErrorClose = () => {
        setShowAlert(false);
        setSubmitError(false);
    }

    const closePhoneNumberValidation = () => {
        setShowValidationModal(false);
        setShowAlert(false);       
        navigate("/WorkerCreated")
    }

    const workerMatchVerified = (authorizationCode, response) => {
        setWorkerAuthorizationCode(authorizationCode);
        setShowWorkerVerificationModal(false);
        setWorkerVerified(true);
        setDisableMatchFields(true);
    }
    
    const handleMatchValuesChanged = (lastName, phoneNumber) => {
        resetMatchVerification();
    }
    
    const continueRequired = () => {
        return !disableMatchFields && !noMatchFoundOnCurrentLastAndPhone;
    }

    const displayFullForm = () => {
        return matchAttemptComplete && (!matchesFound || allMatchesConfirmed || preloadedMatch != null)
    }

    const displayWorkHistory = () => {
        return !onlyPreloadedMatchFound && matchesFound && workerVerified
    }

    return (
        <>
        {(submitting || sendingSMS) && <LoadingIndicator />}

        <ClaycoForm schema={formSchema} onSubmit={submitForm} onError={handleError}
            showAlert={showAlert} alertBody={
                <>
                {submitError ? 
                <FormAlert variant={'danger'} handleClose={handleErrorClose} alertText={alertText}/>
                :
                <FormAlert variant='success' handleClose={handleSuccessClose} alertText={t('RegisterSuccessKey')}/>
                }
                </>
            }
        >
        
            <WorkerMatchVerification verifyLastName={verifyLastName} verifyPhoneNumber={verifyPhoneNumber} show={showWorkerVerificationModal} verified={workerMatchVerified} cancel={resetMatchVerification}/>
            
            <WorkerInformation testMatch={testMatch} preloadedAssociate={preloadedAssociate} handleMatchValuesChanged={handleMatchValuesChanged} mostRecentYesMatchedAssociate={mostRecentYesMatchedAssociate} disableMatchFields={disableMatchFields} continueRequired={continueRequired()}/> 

            {(displayWorkHistory()) && (
                <WorkHistory matchedWorkers={matchedWorkers} matchesConfirmed={matchesConfirmed} resetMatchConfirmation={resetMatchConfirmation}/>
            )}

            <div style={{ display: displayFullForm() ? 'block' : 'none'}} >
              <>
                <AssignmentInfo showLoading={showLoading} preloadedAssignment={preloadedAssignment}/>
                <EmergencyContact preloadedAssociate={preloadedAssociate} mostRecentYesMatchedAssociate={mostRecentYesMatchedAssociate}/>
                <Certificates authorizationCode={workerAuthorizationCode} allMatchesConfirmed={allMatchesConfirmed} yesMatchedAssociates={yesMatchedAssociates} showLoading={showLoading}/>
                <VoluntaryInfo mostRecentYesMatchedAssociate={mostRecentYesMatchedAssociate} showLoading={showLoading}/>
                
                <PhoneNumberValidation show={showValidateModal} associateIdentifier={savedAssociateIds?.identifier} sendSMSMessage={sendSMSMessage} updatePhone={updatePhone} originalSmsResult={submittedPhoneResult} close={closePhoneNumberValidation}/>
                
              </>
            </div>

            {displayFullForm() && (
                <div>
                    <LegalText/>
                    <SpinnerButton showSpin={submitting} variant="primary" type="submit" onClick={() => { setSubmitClicked(true) }}>
                        {t("Submit")}
                    </SpinnerButton>
                    <FormValidationMessage submitAttempted={submitClicked}/>
                </div>
            )}

        </ClaycoForm>
        </>
    )
}

export default Acknowledgement;